import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";

import { LunaChip } from "../FilterPanel/LunaFilterPanel/LunaFilterPanel.utils";
import { useTrafficCopQuery } from "../useTrafficCopQuery";
import { useSearchPageContext } from "../SearchPageProvider";
import { LangstonParameterSchema } from "../LangstonParameterSchema";

interface SearchWithLunaProviderProps {
  children: ReactElement;
}

export const SearchWithLunaContext = createContext<any>(undefined);

const SearchWithLunaProvider = ({ children }: SearchWithLunaProviderProps) => {
  const {
    getters: { searchParams },
    handlers: { submitSearch },
  } = useSearchPageContext();

  const { setSelectedIds: setSelectedTaxonomyTags } =
    useConceptTaxonomyProvider();

  const initialPrompt = useRef<string | null>(null);
  const [trafficCopPrompt, setTrafficCopPrompt] = useState<string | null>(null);
  const [selectedLunaChips, setSelectedLunaChips] = useState<LunaChip[] | null>(
    null,
  );
  const [isInvalidLunaPrompt, setIsInvalidLunaPrompt] =
    useState<boolean>(false);
  const [messageHistory, setMessageHistory] = useState<Record<string, any>>({});
  const [refinementQuestionVariants] = useGetUserExperimentSegment(
    "refinement_question_variants",
  );
  const isRandomSelectionEnabled =
    refinementQuestionVariants === "random_selection";

  const {
    data: trafficCopResponse,
    error: isLunaError,
    isFetching: isTrafficCopFetching,
  } = useTrafficCopQuery({
    prompt: trafficCopPrompt || "",
    enabled: trafficCopPrompt !== null,
    isExperimentModeRandomSelection: isRandomSelectionEnabled,
    chips: selectedLunaChips || [],
    messageHistory,
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const urlPrompt = params.get("prompt");

    // Only react to URL prompt changes (new nav search),
    // not local prompt changes (refinement input).
    if (!urlPrompt) return;

    // If the original prompt has changed, reset the state.
    if (urlPrompt !== initialPrompt.current) {
      initialPrompt.current = urlPrompt;
      setTrafficCopPrompt(urlPrompt);
      setMessageHistory({});
      setSelectedLunaChips([]);
      setSelectedTaxonomyTags(new Set());
      setIsInvalidLunaPrompt(false);
    }
  }, [searchParams.toString()]);

  useEffect(() => {
    if (!isTrafficCopFetching && trafficCopResponse) {
      if (Object.keys(trafficCopResponse.data).length === 0) {
        // If data comes back empty, it's an invalid prompt.
        setIsInvalidLunaPrompt(true);
        return;
      }

      setMessageHistory({
        original_prompt: trafficCopPrompt,
        response: trafficCopResponse.response,
        data: trafficCopResponse.data,
      });

      const trafficCopParams = sanitizeSearchParams(
        buildParamsFromLunaResponse(trafficCopResponse.data),
      );

      const selectedChips = trafficCopResponse.data.chips;
      setSelectedLunaChips(selectedChips);

      // Set taxonomy topic ids.
      const topicIds = selectedChips
        .filter(
          (chip: LunaChip) => chip.type === LangstonParameterSchema.topics.key,
        )
        .map((chip: LunaChip) => chip.value);
      setSelectedTaxonomyTags(new Set(topicIds));

      submitSearch({
        ...trafficCopParams,
      });
    }
  }, [trafficCopPrompt, trafficCopResponse]);

  function buildParamsFromLunaResponse(
    data: Record<string, any>,
  ): Record<string, any> {
    const params: Record<string, any> = {};

    data["chips"].forEach((chip: LunaChip) => {
      if (!params[chip.type]) {
        params[chip.type] = [];
      }
      params[chip.type].push(chip.value);
    });

    if (data?.["search_query_params"]?.["needle"]) {
      params["needle"] = data["search_query_params"]["needle"].replace(
        "+",
        " ",
      );
    }

    return params;
  }

  function sanitizeSearchParams(
    params: Record<string, any>,
  ): Record<string, any> {
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );
  }

  return (
    <SearchWithLunaContext.Provider
      value={{
        isInvalidLunaPrompt,
        isLunaError,
        isTrafficCopFetching,
        selectedLunaChips,
        trafficCopPrompt,
        trafficCopResponse,
        setSelectedLunaChips,
        setTrafficCopPrompt,
      }}
    >
      {children}
    </SearchWithLunaContext.Provider>
  );
};

export const useSearchWithLunaContext = () => {
  const searchWithLunaContext = useContext(SearchWithLunaContext);

  if (searchWithLunaContext === undefined) {
    throw new Error(
      "searchWithLunaContext must be used within a SearchWithLunaProvider.",
    );
  }

  return searchWithLunaContext;
};

SearchWithLunaProvider.displayName = "SearchWithLunaProvider";
export { SearchWithLunaProvider };
